import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaUserShield, FaSpinner } from "react-icons/fa";
import { getApiBase } from "../apiBase";
import { decodeJwt } from "../utils/jwt";

export default function AdminLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${getApiBase()}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();

      if (!res.ok || !data?.token) {
        setError("Invalid admin credentials");
        return;
      }

      const claims = decodeJwt(data.token);
      if (!claims?.is_admin) {
        setError("This account does not have admin access");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("tenant_id", data.config.tenantid);
      localStorage.setItem("company_name", data.config.tenantname);
      navigate("/admin-dashboard");
    } catch {
      setError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center text-white relative overflow-hidden">
      
      <div className="absolute top-[-100px] right-[-100px] w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-[-100px] left-[-100px] w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-md p-8 bg-[#1A1A2E] border border-white/5 rounded-2xl shadow-2xl relative z-10">
        
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center border border-cyan-500/30 mb-4">
            <FaUserShield className="text-2xl text-cyan-400" />
          </div>
          <h2 className="text-2xl font-bold">Admin Access</h2>
          <p className="text-gray-500 text-sm mt-1">
            Restricted control panel login
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">

          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold tracking-widest">
              Admin Email
            </label>
            <input
              type="email"
              placeholder="Enter admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-[#0F0F1A] border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition text-sm"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold tracking-widest">
              Password
            </label>
            <div className="relative mt-2">
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#0F0F1A] border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition text-sm"
              />
              <FaLock className="absolute right-3 top-3.5 text-gray-500 text-xs" />
            </div>
          </div>

          {error && (
            <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 p-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin text-sm" />
                Authenticating...
              </>
            ) : (
              "Login to Admin"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}